import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ROUTES } from "@/config/routes";
import { site } from "@/config/site";

const toc = [
  { id: "privacy", label: "Privacy Policy", route: ROUTES.privacy },
  { id: "terms", label: "Terms of Service", route: ROUTES.terms },
  { id: "stripe", label: "Stripe & billing", route: `${ROUTES.legal}#stripe` },
] as const;

export type LegalFocusSection = "privacy" | "terms" | "stripe";

type LegalPageProps = {
  focusSection?: LegalFocusSection;
};

export function LegalPage({ focusSection }: LegalPageProps) {
  const navigate = useNavigate();

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash === "privacy") {
      navigate(ROUTES.privacy, { replace: true });
      return;
    }
    if (hash === "terms") {
      navigate(ROUTES.terms, { replace: true });
      return;
    }

    const section = focusSection ?? (hash === "stripe" ? "stripe" : undefined);
    if (!section) {
      return;
    }
    requestAnimationFrame(() => {
      document.getElementById(section)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [focusSection, navigate]);

  return (
    <div className="pub-page">
      <div className="pub-page-body">
        <div className="pub-docs-layout">
          <aside className="pub-docs-sidebar">
            <h2>On this page</h2>
            {toc.map((item) =>
              item.id === "stripe" ? (
                <a key={item.id} href={`#${item.id}`}>
                  {item.label}
                </a>
              ) : (
                <Link key={item.id} to={item.route}>
                  {item.label}
                </Link>
              ),
            )}
          </aside>

          <article className="pub-legal-article">
            <h1 className="pub-legal-title">Privacy &amp; Terms</h1>

            <p className="pub-legal-updated">
              Effective date · {site.legalEffectiveDate} · Contact:{" "}
              <a href={`mailto:${site.supportEmail}`} className="pub-inline-link">
                {site.supportEmail}
              </a>
            </p>

            <p className="pub-legal-intro">
              {site.brandName} operates {site.name} at{" "}
              <a href={site.appHost} className="pub-inline-link">
                app.qiqiforge.io
              </a>
              , a Stripe App for invoice PDF export.
            </p>

            <section id="privacy" className="pub-legal-section">
              <h2>Privacy Policy</h2>
              <p>
                We read invoice-related data from your connected Stripe account only when you
                search or export. We do not sell personal information.
              </p>
              <p>
                On install we may store merchant contact details from your Stripe Account (email,
                business name, country) for support and product communications about this app. We
                do not use your Stripe Customers&apos; emails for our marketing.
              </p>
              <p>
                We store account settings, export job metadata (not PDF content), optional
                notification email, and billing identifiers. We also count which product pages you
                open (path and Stripe account only) to see which features are used. Export ZIPs are stored in Google Cloud
                Storage (GCS) for 15 days, then deleted. Uninstall the App from Stripe Dashboard at any time, or email{" "}
                <a href={`mailto:${site.supportEmail}`} className="pub-inline-link">
                  {site.supportEmail}
                </a>{" "}
                for access, correction, or deletion.
              </p>
            </section>

            <section id="terms" className="pub-legal-section">
              <h2>Terms of Service</h2>
              <p>
                {site.name} provides PDF export for Stripe merchants. You must be authorized on
                the connected account. Stripe&apos;s terms apply to your Stripe account; we are not
                a payment processor.
              </p>
              <p>
                Starter is free on install. Pro is $10 per month via Stripe. Subscriptions
                renew monthly until canceled in Stripe Customer Portal. Canceling stops the
                next renewal; the current month stays active. Use the service lawfully.
              </p>
              <p>
                THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; WITHOUT WARRANTIES. LIABILITY IS LIMITED
                TO FEES PAID IN THE PRIOR 12 MONTHS OR USD $100, WHICHEVER IS GREATER. Wyoming,
                USA law governs these Terms.
              </p>
            </section>

            <section id="stripe" className="pub-legal-section">
              <h2>Stripe &amp; billing</h2>
              <p>
                Invoice data is read from your Stripe Connect account. Subscription payments run
                on {site.brandName}&apos;s Stripe platform account. Search, export, and downloads
                happen on the web app; the Dashboard drawer is an entry point only.
              </p>
            </section>

            <p className="pub-legal-footer-link">
              <Link to={ROUTES.help} className="pub-inline-link">
                Help &amp; support
              </Link>
            </p>
          </article>
        </div>
      </div>
    </div>
  );
}
